/**
 * Create-Wizard — Modell (G1). Reine Logik: Form-Shape und Step-Validierung; die Anfrage an den
 * Server baut `wizardRequest.ts`.
 *
 * Draft-first: der Abschluss erzeugt genau einen lokalen Draft (kein Channex-Request
 * aus dem Create). Schritt 1 wählt Standort als XOR — bestehendes Objekt ODER neuer
 * Standort — und liefert damit den `objectRef` des Contracts (E1).
 */
import { z } from 'zod';
import type { PropertyType } from '@/shared/api';

export type LocationMode = 'existing' | 'new';
export type WizardStep = 0 | 1 | 2;
export const WIZARD_STEPS: readonly { id: WizardStep; label: string }[] = [
  { id: 0, label: 'Standort' },
  { id: 1, label: 'Apartment' },
  { id: 2, label: 'Betrieb' },
];

export interface WizardForm {
  locationMode: LocationMode;
  existingObjectId: string;
  existingObjectLabel: string;
  addressQuery: string;
  streetName: string;
  houseNumber: string;
  city: string;
  postalCode: string;
  countryCode: string;
  latitude: number | null;
  longitude: number | null;
  objectName: string;
  needsAddressReview: boolean;
  name: string;
  slug: string;
  description: string;
  propertyType: PropertyType;
  timezone: string;
  occupancyAdults: number;
  occupancyChildren: number;
  bedrooms: number;
  bathrooms: number;
  currency: string;
  cleaningFee: number;
  baseNightlyRate: number;
}

export function emptyWizardForm(): WizardForm {
  return {
    locationMode: 'new',
    existingObjectId: '',
    existingObjectLabel: '',
    addressQuery: '',
    streetName: '',
    houseNumber: '',
    city: '',
    postalCode: '',
    countryCode: 'DE',
    latitude: null,
    longitude: null,
    objectName: '',
    needsAddressReview: true,
    name: '',
    slug: '',
    description: '',
    propertyType: 'Apartment',
    timezone: 'Europe/Berlin',
    occupancyAdults: 2,
    occupancyChildren: 0,
    bedrooms: 1,
    bathrooms: 1,
    currency: 'EUR',
    cleaningFee: 50,
    baseNightlyRate: 100,
  };
}

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/** Objektname-Vorschlag aus Straße + Hausnummer (Fallback Stadt) — spiegelt das Backend (E3). */
export function suggestObjectName(streetName: string, houseNumber: string, city: string): string {
  const line = `${streetName.trim()} ${houseNumber.trim()}`.trim();
  return (line || city.trim()).slice(0, 120);
}

export function isCompleteAddress(
  address: Pick<WizardForm, 'streetName' | 'houseNumber' | 'city' | 'postalCode' | 'countryCode'>,
): boolean {
  return Boolean(
    address.streetName.trim() &&
    address.houseNumber.trim() &&
    address.city.trim() &&
    address.postalCode.trim() &&
    address.countryCode.trim().length === 2,
  );
}

export const wizardSchema = z
  .object({
    locationMode: z.enum(['existing', 'new']),
    existingObjectId: z.string(),
    streetName: z.string(),
    houseNumber: z.string(),
    city: z.string(),
    postalCode: z.string(),
    countryCode: z.string(),
    objectName: z.string(),
    name: z.string().min(2, 'Mindestens 2 Zeichen').max(200),
    slug: z
      .string()
      .min(2, 'Mindestens 2 Zeichen')
      .max(80)
      .regex(/^[a-z0-9-]+$/, 'Nur Kleinbuchstaben, Ziffern, Bindestriche'),
    propertyType: z.enum(['Apartment', 'House', 'Villa', 'Studio']),
    timezone: z.string().min(1).max(64),
    occupancyAdults: z.coerce.number().int().min(1).max(30),
    occupancyChildren: z.coerce.number().int().min(0).max(30),
    bedrooms: z.coerce.number().int().min(0).max(20),
    bathrooms: z.coerce.number().min(0).max(20),
    currency: z.string().length(3, 'ISO-Currency (z. B. EUR)'),
    cleaningFee: z.coerce.number().min(0).max(10_000),
    baseNightlyRate: z.coerce.number().min(0).max(100_000),
  })
  .superRefine((v, ctx) => {
    if (v.locationMode === 'existing') {
      if (!v.existingObjectId) {
        ctx.addIssue({ code: 'custom', path: ['existingObjectId'], message: 'Objekt wählen' });
      }
      return;
    }
    for (const [field, label] of [
      ['streetName', 'Straße fehlt'],
      ['houseNumber', 'Hausnummer fehlt'],
      ['city', 'Stadt fehlt'],
      ['postalCode', 'PLZ fehlt'],
    ] as const) {
      if (!String(v[field]).trim()) ctx.addIssue({ code: 'custom', path: [field], message: label });
    }
    if (v.countryCode.length !== 2) {
      ctx.addIssue({ code: 'custom', path: ['countryCode'], message: 'Land wählen' });
    }
    if (!v.objectName.trim()) {
      ctx.addIssue({ code: 'custom', path: ['objectName'], message: 'Objektname fehlt' });
    }
  });

export function stepFields(step: WizardStep, mode: LocationMode): (keyof WizardForm)[] {
  if (step === 0) {
    return mode === 'existing'
      ? ['existingObjectId']
      : ['streetName', 'houseNumber', 'city', 'postalCode', 'countryCode', 'objectName'];
  }
  if (step === 1) {
    return [
      'name',
      'slug',
      'propertyType',
      'timezone',
      'occupancyAdults',
      'occupancyChildren',
      'bedrooms',
      'bathrooms',
    ];
  }
  return ['currency', 'cleaningFee', 'baseNightlyRate'];
}
